import { createContext, useState, useEffect, useContext } from 'react';
import API_BASE from '../utils/api';

export const ClientAuthContext = createContext();

export function useClientAuth() {
  return useContext(ClientAuthContext);
}

const STORAGE_KEY = 'client_session';

function getInitialClientAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function ClientAuthProvider({ children }) {
  const [clientAuth, setClientAuth] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const saved = getInitialClientAuth();
    if (saved?.clientId) {
      // Verify session is still valid
      fetch(`${API_BASE}/api/client/auth/me`, {
        credentials: 'include',
        headers: saved.token ? { Authorization: `Bearer ${saved.token}` } : undefined,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((profile) => {
          if (profile?.clientId) {
            setClientAuth({ ...saved, ...profile });
          } else {
            localStorage.removeItem(STORAGE_KEY);
            setClientAuth(null);
          }
        })
        .catch(() => {
          setClientAuth(saved);
        })
        .finally(() => setIsHydrated(true));
    } else {
      setIsHydrated(true);
    }
  }, []);

  function clientLogin(data) {
    const session = {
      clientId: data.clientId,
      name: data.name,
      email: data.email,
      designerUserId: data.designerUserId,
      permissions: data.permissions || {},
      projectIds: data.projectIds || [],
      token: data.token,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setClientAuth(session);
  }

  async function clientLogout() {
    try {
      await fetch(`${API_BASE}/api/client/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // best-effort
    }
    localStorage.removeItem(STORAGE_KEY);
    setClientAuth(null);
  }

  return (
    <ClientAuthContext.Provider value={{ clientAuth, isHydrated, clientLogin, clientLogout }}>
      {children}
    </ClientAuthContext.Provider>
  );
}
